import { supabase } from '../lib/supabase';

// WhatsApp Integration Service
export const whatsappService = {
  // Get WhatsApp integration settings
  async getSettings() {
    try {
      const { data, error } = await supabase?.from('whatsapp_settings')?.select('*')?.single();

      if (error) {
        throw error;
      }

      return { data, error: null };
    } catch (error) {
      return { data: null, error: error?.message };
    }
  },

  // Update WhatsApp settings
  async updateSettings(settings) {
    try {
      // First, try to get existing settings
      const { data: existingSettings } = await supabase?.from('whatsapp_settings')?.select('id')?.single();

      let data, error;

      if (existingSettings?.id) {
        // Update existing settings
        const result = await supabase?.from('whatsapp_settings')
          ?.update(settings)
          ?.eq('id', existingSettings.id)
          ?.select()
          ?.single();
        data = result.data;
        error = result.error;
      } else {
        // Insert new settings
        const result = await supabase?.from('whatsapp_settings')
          ?.insert(settings)
          ?.select()
          ?.single();
        data = result.data;
        error = result.error;
      }

      if (error) {
        throw error;
      }

      return { data, error: null };
    } catch (error) {
      return { data: null, error: error?.message };
    }
  },

  // Message Templates
  async getMessageTemplates() {
    try {
      const { data, error } = await supabase?.from('message_templates')?.select(`
          *,
          created_by_profile:user_profiles!message_templates_created_by_fkey(full_name)
        `)?.order('created_at', { ascending: false });

      if (error) {
        throw error;
      }

      return { data, error: null };
    } catch (error) {
      return { data: null, error: error?.message };
    }
  },

  async createMessageTemplate(template) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();

      const { data, error } = await supabase?.from('message_templates')?.insert({
        ...template,
        created_by: user?.id
      })?.select()?.single();

      if (error) {
        throw error;
      }

      return { data, error: null };
    } catch (error) {
      return { data: null, error: error?.message };
    }
  },

  async updateMessageTemplate(id, template) {
    try {
      const { data, error } = await supabase?.from('message_templates')?.update(template)?.eq('id', id)?.select()?.single();

      if (error) {
        throw error;
      }

      return { data, error: null };
    } catch (error) {
      return { data: null, error: error?.message };
    }
  },

  async deleteMessageTemplate(id) {
    try {
      const { error } = await supabase?.from('message_templates')?.delete()?.eq('id', id);

      if (error) {
        throw error;
      }

      return { error: null };
    } catch (error) {
      return { error: error?.message };
    }
  },

  // WhatsApp Messages
  async getMessages(filters = {}) {
    try {
      let query = supabase?.from('whatsapp_messages')?.select(`
          *,
          recipient:user_profiles!whatsapp_messages_recipient_id_fkey(full_name, phone),
          appointment:appointments!whatsapp_messages_appointment_id_fkey(preferred_date, preferred_time),
          template:message_templates!whatsapp_messages_template_id_fkey(name),
          sender:user_profiles!whatsapp_messages_sent_by_fkey(full_name)
        `)?.order('created_at', { ascending: false });

      // Apply filters
      if (filters?.status) {
        query = query?.eq('status', filters?.status);
      }
      if (filters?.message_type) {
        query = query?.eq('message_type', filters?.message_type);
      }
      if (filters?.recipient_id) {
        query = query?.eq('recipient_id', filters?.recipient_id);
      }
      if (filters?.date_from) {
        query = query?.gte('created_at', filters?.date_from);
      }
      if (filters?.date_to) {
        query = query?.lte('created_at', filters?.date_to);
      }

      const { data, error } = await query;

      if (error) {
        throw error;
      }

      return { data, error: null };
    } catch (error) {
      return { data: null, error: error?.message };
    }
  },

  async sendMessage(messageData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();

      const { data, error } = await supabase?.from('whatsapp_messages')?.insert({
        ...messageData,
        sent_by: user?.id,
        status: 'pending'
      })?.select(`
          *,
          recipient:user_profiles!whatsapp_messages_recipient_id_fkey(full_name, phone)
        `)?.single();

      if (error) {
        throw error;
      }

      // Here you would integrate with WhatsApp API
      // For now, we'll simulate the API call
      await this.simulateWhatsAppAPI(data);

      return { data, error: null };
    } catch (error) {
      return { data: null, error: error?.message };
    }
  },

  async simulateWhatsAppAPI(message) {
    // Simulate API call delay
    await new Promise(resolve => setTimeout(resolve, 1000));

    // Update message status
    const { error } = await supabase?.from('whatsapp_messages')?.update({
      status: 'sent',
      whatsapp_message_id: `wa_${Date.now()}`,
      delivery_timestamp: new Date()?.toISOString()
    })?.eq('id', message?.id);

    if (error) {
      console.error('Error updating message status:', error);
    }
  },

  // Bulk Campaigns
  async getBulkCampaigns() {
    try {
      const { data, error } = await supabase?.from('bulk_campaigns')?.select(`
          *,
          template:message_templates!bulk_campaigns_template_id_fkey(name, template_text),
          creator:user_profiles!bulk_campaigns_created_by_fkey(full_name)
        `)?.order('created_at', { ascending: false });

      if (error) {
        throw error;
      }

      return { data, error: null };
    } catch (error) {
      return { data: null, error: error?.message };
    }
  },

  async createBulkCampaign(campaign) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();

      const { data, error } = await supabase?.from('bulk_campaigns')?.insert({
        ...campaign,
        created_by: user?.id
      })?.select(`
          *,
          template:message_templates!bulk_campaigns_template_id_fkey(name, template_text)
        `)?.single();

      if (error) {
        throw error;
      }

      return { data, error: null };
    } catch (error) {
      return { data: null, error: error?.message };
    }
  },

  // Delivery Reports
  async getDeliveryReports(messageId = null) {
    try {
      let query = supabase?.from('delivery_reports')?.select(`
          *,
          message:whatsapp_messages!delivery_reports_message_id_fkey(
            message_content,
            phone_number,
            recipient:user_profiles!whatsapp_messages_recipient_id_fkey(full_name)
          )
        `)?.order('timestamp', { ascending: false });

      if (messageId) {
        query = query?.eq('message_id', messageId);
      }

      const { data, error } = await query;

      if (error) {
        throw error;
      }

      return { data, error: null };
    } catch (error) {
      return { data: null, error: error?.message };
    }
  },

  // Analytics
  async getAnalytics(dateRange = {}) {
    try {
      let query = supabase?.from('whatsapp_messages')?.select('status, message_type, created_at');

      if (dateRange?.from) {
        query = query?.gte('created_at', dateRange?.from);
      }
      if (dateRange?.to) {
        query = query?.lte('created_at', dateRange?.to);
      }

      const { data, error } = await query;

      if (error) {
        throw error;
      }

      // Process analytics data
      const analytics = {
        total_messages: data?.length || 0,
        sent: data?.filter(m => m?.status === 'sent')?.length || 0,
        delivered: data?.filter(m => m?.status === 'delivered')?.length || 0,
        read: data?.filter(m => m?.status === 'read')?.length || 0,
        failed: data?.filter(m => m?.status === 'failed')?.length || 0,
        by_type: {}
      };

      // Group by message type
      data?.forEach(message => {
        if (!analytics?.by_type?.[message?.message_type]) {
          analytics.by_type[message.message_type] = 0;
        }
        analytics.by_type[message.message_type]++;
      });

      return { data: analytics, error: null };
    } catch (error) {
      return { data: null, error: error?.message };
    }
  },

  // Real-time subscriptions
  subscribeToMessages(callback) {
    return supabase?.channel('whatsapp_messages')?.on(
      'postgres_changes',
      { event: '*', schema: 'public', table: 'whatsapp_messages' },
      callback
    )?.subscribe();
  },

  subscribeToDeliveryReports(callback) {
    return supabase?.channel('delivery_reports')?.on(
      'postgres_changes',
      { event: '*', schema: 'public', table: 'delivery_reports' },
      callback
    )?.subscribe();
  },

  // Utility functions
  async sendAppointmentConfirmation(appointmentId) {
    try {
      const { error } = await supabase?.rpc('send_appointment_confirmation', {
        appointment_uuid: appointmentId
      });

      if (error) {
        throw error;
      }

      return { error: null };
    } catch (error) {
      return { error: error?.message };
    }
  },

  replaceVariables(template, variables) {
    let result = template;
    Object.keys(variables)?.forEach(key => {
      const regex = new RegExp(`{{${key}}}`, 'g');
      result = result?.replace(regex, variables?.[key] || '');
    });
    return result;
  }
};